/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { accessSyncDirectoryGroups } from "../funcs/accessSyncDirectoryGroups.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { SyncDirectoryGroupsResult } from "../models/components/syncdirectorygroupsresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SyncDirectoryGroupsRequest,
  SyncDirectoryGroupsSecurity,
} from "../models/operations/syncdirectorygroups.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SyncDirectoryGroupsMutationVariables = {
  request?: SyncDirectoryGroupsRequest | undefined;
  security?: SyncDirectoryGroupsSecurity | undefined;
  options?: RequestOptions;
};

export type SyncDirectoryGroupsMutationData = SyncDirectoryGroupsResult;

export type SyncDirectoryGroupsMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * syncDirectoryGroups access
 *
 * @remarks
 * Fetch the organization's directory groups from WorkOS and save any that are new or changed.
 */
export function useSyncDirectoryGroupsMutation(
  options?: MutationHookOptions<
    SyncDirectoryGroupsMutationData,
    SyncDirectoryGroupsMutationError,
    SyncDirectoryGroupsMutationVariables
  >,
): UseMutationResult<
  SyncDirectoryGroupsMutationData,
  SyncDirectoryGroupsMutationError,
  SyncDirectoryGroupsMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSyncDirectoryGroupsMutation(client, options),
    ...options,
  });
}

export function mutationKeySyncDirectoryGroups(): MutationKey {
  return ["@gram/client", "access", "syncDirectoryGroups"];
}

export function buildSyncDirectoryGroupsMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SyncDirectoryGroupsMutationVariables,
  ) => Promise<SyncDirectoryGroupsMutationData>;
} {
  return {
    mutationKey: mutationKeySyncDirectoryGroups(),
    mutationFn: function syncDirectoryGroupsMutationFn({
      request,
      security,
      options,
    }): Promise<SyncDirectoryGroupsMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(accessSyncDirectoryGroups(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
